import { Box, LinearProgress, Typography } from "@mui/material"
import { WarningOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import type { ReactElement } from "react"
import React, { useEffect, useState } from "react"

import useInstructionActions from "../../hooks/useInstructionActions"
import useProductsByInstructionList from "../../hooks/useProductsByInstructionList"
import useSearchList from "../../hooks/useSearchList"
import { AddProductDialog, ProductAddMoreDialog } from "../dialogs"
import ActivateInstructionButton from "../instructionProducts/ActivateInstructionButton"

import AssociatedProductsArea from "./AssociatedProductsArea"
import AssociateProductButton from "./AssociateProductButton"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import type { Instruction } from "@/graphql/codegen/graphql"
import { useInstructionFileUpload } from "@/screens/ManageProducts/components/Instructions/hooks/useInstructionFileUpload"

enum Dialog {
  ASSOCIATE_PRODUCTS = "ASSOCIATE_PRODUCTS",
  ASSOCIATE_MORE = "ASSOCIATE_MORE",
}

interface ExpandedInstructionRowProps {
  row: Row<Instruction>
  shouldRefetch: boolean
  setShouldRefetch: (prevState: (prev: Record<number, boolean>) => Record<number, boolean>) => void
}

const ExpandedInstructionRow = ({
  row,
  shouldRefetch,
  setShouldRefetch,
}: ExpandedInstructionRowProps): ReactElement => {
  const instruction = row.original
  const instructionId = instruction.instructionId
  const isActiveInstruction = instruction.isActive
  const [showAddProductDialog, setShowAddProductDialog] = useState<Dialog | null>(null)
  const { products, fetchingProducts, reexecuteQuery } = useProductsByInstructionList({
    instructionId,
    pause: !isActiveInstruction,
  })
  const { fileData } = useInstructionFileUpload({ instructionId })

  const { updateInstructionStatus } = useInstructionActions()
  const { refetchData } = useSearchList()

  useEffect(() => {
    if (!shouldRefetch) {
      return
    }
    reexecuteQuery()
    setShouldRefetch((prev: Record<number, boolean>) => {
      return {
        ...prev,
        [row.id]: false,
      }
    })
  }, [reexecuteQuery, row.id, setShouldRefetch, shouldRefetch])

  const associatedProductIds = products.map((p) => p.productId)

  const toggleAddProductDialog = () => {
    setShowAddProductDialog(showAddProductDialog === Dialog.ASSOCIATE_PRODUCTS ? null : Dialog.ASSOCIATE_PRODUCTS)
  }
  const toggleAddMoreProductDialog = () => {
    setShowAddProductDialog(showAddProductDialog === Dialog.ASSOCIATE_MORE ? null : Dialog.ASSOCIATE_MORE)
  }

  const onAddProductSuccess = () => {
    reexecuteQuery()
    toggleAddMoreProductDialog()
  }
  const activateProduct = async () => {
    await updateInstructionStatus({
      instructionId,
      isActive: true,
    })
    refetchData()
  }

  const hasProductsData = isActiveInstruction && !fetchingProducts && !!products.length
  const hasNoProducts = isActiveInstruction && !fetchingProducts && !products.length
  const instructionImage = fileData[0]

  return (
    <Box className='flex flex-col'>
      <Box className='mx-6 my-4 flex items-center'>
        {instructionImage?.file?.originalName && (
          <img
            className='mr-3'
            src={instructionImage.url}
            alt={instructionImage.file.originalName}
            width={48}
            height={48}
          />
        )}
        <Typography>{instruction.description}</Typography>
      </Box>
      {!isActiveInstruction && (
        <InformationMissingAlert
          className='mx-6 mb-4 mt-2 px-6 pb-2 pt-4'
          icon={<WarningOutlined color='error' className='p-0' />}
          title='Activate Instruction in order to associate products to it'
          action={<ActivateInstructionButton onClick={activateProduct} />}
          description={""}
        />
      )}
      {hasNoProducts && (
        <InformationMissingAlert
          className='mx-6 mb-4 mt-2 px-6 pb-2 pt-4'
          title='Associate Products to this instruction'
          action={<AssociateProductButton onClick={toggleAddProductDialog} />}
          description={""}
        />
      )}
      {fetchingProducts && <LinearProgress className='sticky top-0 z-10' />}
      {hasProductsData && <AssociatedProductsArea products={products} openAddProductDialog={toggleAddProductDialog} />}
      {showAddProductDialog === Dialog.ASSOCIATE_PRODUCTS && (
        <AddProductDialog
          instructionId={instruction.instructionId}
          instructionName={instruction.name}
          associatedProductIds={associatedProductIds}
          onClose={toggleAddProductDialog}
          onSuccess={onAddProductSuccess}
        />
      )}
      {showAddProductDialog === Dialog.ASSOCIATE_MORE && (
        <ProductAddMoreDialog onClose={toggleAddMoreProductDialog} onAddMore={toggleAddProductDialog} />
      )}
    </Box>
  )
}

export default ExpandedInstructionRow
